import { toast } from "sonner";
import { i18next } from "@/app/i18n";
import { log } from "@/app/observability";
import { setErrorNotifier } from "@/app/query/query-client";
import type { ApiErrorCode } from "@shared/contracts";

/**
 * THE ONE WAY TO SAY SOMETHING TO THE USER IN PASSING.
 *
 * The query cache already owns FAILURES — one message and one report per failed read or
 * write, decided in `app/query/query-client.ts` and shown by `showFailure` at the bottom of
 * this file. This is the other half: everything the cache cannot know about. A copy that
 * landed on the clipboard, a permission the phone refused, a photo too large to upload, a
 * note saved. Those have no error to attach to and no cache entry to hang off, and before
 * this seam existed the only honest options were a `useState` string per screen or nothing
 * at all — which is what `NoteComposer` had.
 *
 * ## A KEY, NEVER A SENTENCE
 *
 * `announce("notes:compose.saved")`, not `announce("Note saved")`. Two things depend on it:
 * the sentence is translated once, in the file that holds every other sentence for that
 * feature, and the RECORD is a stable identifier. A log line reading `notes:compose.saved`
 * can be counted across releases and across languages; one reading "Notiz gespeichert"
 * cannot be counted at all.
 *
 * ## SEVERITY DECIDES WHETHER IT IS RECORDED
 *
 * A warning or an error is always recorded — those are the ones somebody will want to count
 * later, and making that optional means the day it matters it will have been off. A success
 * or an info opts in with `record: true`, because "copied to clipboard" ten thousand times a
 * day is a bill, not a signal.
 *
 * This is NOT `reportError`: nothing here files an issue for anybody to fix. A warning worth
 * a person's attention is a failure, and a failure goes through the query cache.
 */
export type AnnouncementSeverity = "success" | "info" | "warning" | "error";

export type AnnounceOptions = {
  /** Defaults to `info`. `warning` and `error` are always recorded. */
  severity?: AnnouncementSeverity;
  /** Interpolated into the message. Never build a sentence by concatenating fragments. */
  values?: Record<string, string | number>;
  /** One optional action, its label a key like the message. */
  action?: { labelKey: string; onClick: () => void };
  /** Record a success or an info too. Ignored for a warning or an error — those always are. */
  record?: boolean;
  /** Attached to the record. Same scrubbing rules as everywhere else: ids, never content. */
  context?: Record<string, unknown>;
};

/**
 * The LEVEL, not the function. Holding the function would freeze whichever implementation
 * `log` had at module-evaluation time, and the reporting seam deliberately swaps its
 * implementation later — `initObservability()` replaces the console reporter with the real
 * one once the keys are known. Looking the level up per call is what keeps an announcement
 * raised after start-up going to the reporter that is actually installed.
 */
const RECORD_AS: Record<AnnouncementSeverity, "info" | "warn" | "error"> = {
  success: "info",
  info: "info",
  warning: "warn",
  error: "error",
};

export function announce(messageKey: string, options: AnnounceOptions = {}): void {
  const { severity = "info", values, action, record = false, context } = options;

  const alwaysRecorded = severity === "warning" || severity === "error";
  if (alwaysRecorded || record) {
    // The KEY, deliberately — see the note above on what a countable record looks like.
    log[RECORD_AS[severity]](messageKey, { ...context, severity });
  }

  /**
   * Translated at the moment it is shown, from the i18next instance rather than a `t` from a
   * hook. That is what lets a message be raised from outside React — a platform callback, a
   * cache handler — and still follow a language the user changed a second ago.
   */
  toast[severity](translate(messageKey, values), {
    action: action && { label: translate(action.labelKey), onClick: action.onClick },
  });
}

/**
 * THE QUERY CACHE'S CHANNEL, wired up once at start-up by `AppProviders`.
 *
 * Separate from `announce` on purpose, in two ways. It takes a CODE rather than a key,
 * because the app's error vocabulary is what a failure carries end to end. And it records
 * nothing: the cache has already reported this failure with the domain and action attached,
 * and a log line here would be the same event twice under two names.
 *
 * The service's own wording wins when there is any. `Notifier` has carried a second
 * parameter for it since the type was written, and until now the cache passed nothing and
 * the placeholder toast ignored it — so a service that took the trouble to explain a refusal
 * had that explanation dropped on the floor and replaced by the generic line for its code.
 */
export function showFailure(code: ApiErrorCode, message?: string): void {
  toast.error(message ?? translate(`common:errors.${code}`));
}

/** Installs the above. Explicit call, never an import side effect. */
export function installFailureNotifier(): void {
  setErrorNotifier(showFailure);
}

/**
 * ONE CAST, AND WHY IT IS HERE RATHER THAN AT EVERY CALL.
 *
 * `t` is typed against the resource tree, and this project assembles that tree with a
 * build-time glob over every feature's locale folder — so there is no literal union for the
 * types to check a key against, and the overload picked for a plain `string` resolves to a
 * detailed-result object rather than a sentence. Narrowing the function once, here, keeps
 * every caller honest about what it gets back. Key parity across languages is checked by
 * `make check-arch`, which is the property that actually breaks a screen.
 *
 * Capturing the reference is safe and checked: i18next binds every prototype method to the
 * instance in its CONSTRUCTOR, before `initI18n()` runs, and `t` reads the current language
 * off the instance on each call — so a message raised after the user switches language is
 * still in the language they switched to.
 */
const translate = i18next.t as unknown as (
  key: string,
  values?: Record<string, string | number>,
) => string;
